import type {
  ExpenseReceiptResponseDto,
  ExpenseReceiptUrlsResponseDto,
  ExpensesControllerListExpensesParams,
} from '@trellis/api'
import type {
  CreateBusinessExpense,
  CreateReimbursementExpense,
  ExpenseResponse,
  RequestResult,
  UpdateExpense,
} from '@trellis/shared'

import { env } from '@/env'
import { apiClient } from '@/lib/api-client'
import { request, requestPaged } from '@/lib/request'
import { getStoredTenantSlug } from '@/lib/tenant'

export type ExpenseListParams = ExpensesControllerListExpensesParams

export const listExpenses = (params: ExpenseListParams = {}) =>
  requestPaged<ExpenseResponse>(() =>
    apiClient.api.expensesControllerListExpenses(params),
  )

export const getExpense = (id: string) =>
  request<ExpenseResponse>(() => apiClient.api.expensesControllerFindExpense({ id }))

export const createBusinessExpense = (data: CreateBusinessExpense) =>
  request<ExpenseResponse>(() =>
    apiClient.api.expensesControllerCreateBusinessExpense(data),
  )

export const createReimbursementExpense = (data: CreateReimbursementExpense) =>
  request<ExpenseResponse>(() =>
    apiClient.api.expensesControllerCreateReimbursementExpense(data),
  )

export const updateExpense = (id: string, data: UpdateExpense) =>
  request<ExpenseResponse>(() =>
    apiClient.api.expensesControllerUpdateExpense({ id }, data),
  )

export const deleteExpense = (id: string) =>
  request<void>(() => apiClient.api.expensesControllerDeleteExpense({ id }))

export const duplicateExpense = (id: string) =>
  request<ExpenseResponse>(() => apiClient.api.expensesControllerDuplicateExpense({ id }))

export const billExpense = (expenseId: string, invoiceId: string) =>
  request<ExpenseResponse>(() =>
    apiClient.api.expensesControllerBillExpense({ id: expenseId }, { invoiceId }),
  )

export const uploadReceipt = async (
  expenseId: string,
  file: File,
): Promise<RequestResult<ExpenseReceiptResponseDto>> => {
  const formData = new FormData()
  formData.append('file', file)

  const headers = new Headers({ Accept: 'application/json' })
  const slug = getStoredTenantSlug()
  if (slug) headers.set('X-Tenant-Slug', slug)

  try {
    const response = await fetch(
      `${env.VITE_API_BASE_URL}/api/expenses/${expenseId}/receipt`,
      { method: 'POST', body: formData, credentials: 'include', headers },
    )
    const body = await response.json()
    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: body.error?.code ?? 'API_ERROR',
          message: body.error?.message ?? 'Upload failed',
        },
      }
    }
    return { status: response.status, data: body.data, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 0,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message: caughtError instanceof Error ? caughtError.message : 'Upload failed',
      },
    }
  }
}

export const getReceiptUrl = (expenseId: string) =>
  request<ExpenseReceiptUrlsResponseDto>(() =>
    apiClient.api.expensesControllerGetReceipt({ id: expenseId }),
  )

export const deleteReceipt = (expenseId: string) =>
  request<void>(() => apiClient.api.expensesControllerDeleteReceipt({ id: expenseId }))

export const claimExpense = (id: string) =>
  updateExpense(id, { type: 'BUSINESS', claimed: true })

export const unclaimExpense = (id: string) =>
  updateExpense(id, { type: 'BUSINESS', claimed: false })

export const assignTagsToExpense = (expenseId: string, tagIds: string[]) =>
  request<void>(() =>
    apiClient.api.expensesControllerAssignTags({ id: expenseId }, { tagIds }),
  )

export const removeTagFromExpense = (expenseId: string, tagId: string) =>
  request<void>(() => apiClient.api.expensesControllerRemoveTag({ id: expenseId, tagId }))
